import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { nextSort, type SortState } from '../lib/table-sort';
import { useDebounce } from './use-debounce';
import { useOrgParam } from './use-org-param';

export interface MarketFilters {
  search: string;
  kind: string;
  models: string[];
  homeBase: string;
  charterNames: string[];
  minLength?: number;
  maxLength?: number;
  minCabins?: number;
  maxCabins?: number;
  minBerths?: number;
  maxBerths?: number;
  minYear?: number;
  maxYear?: number;
  minWc?: number;
  minPeople?: number;
  minBeam?: number;
  maxBeam?: number;
  minDraught?: number;
  maxDraught?: number;
  equipmentIds: number[];
  // Cascading location filter (Market Explorer, issue #95). Country is
  // single-select and drives the cascade — picking a country narrows the
  // sailing-area list to areas within it, and choosing sailing areas
  // narrows the base list. Both sailing area and base are multi-select.
  // Child levels are invalid (and cleared) without the parent set.
  countryPid?: string;
  sailingAreaPids: string[];
  basePids: string[];
}

export type MarketTab = 'browse' | 'tracked';

/** Columns the market lists can be sorted by — mirrors the API's sortBy whitelist. */
export const MARKET_SORT_COLUMNS = [
  'name',
  'kind',
  'year',
  'lengthM',
  'cabins',
  'createdAt',
  'equipmentScore',
] as const;

export type MarketSortColumn = (typeof MARKET_SORT_COLUMNS)[number];

export function isMarketSortColumn(value: string | undefined): value is MarketSortColumn {
  return value !== undefined && (MARKET_SORT_COLUMNS as readonly string[]).includes(value);
}

export interface MarketSearchParams {
  tab?: MarketTab;
  search?: string;
  kind?: string;
  models?: string;
  homeBase?: string;
  charterNames?: string;
  minLength?: number;
  maxLength?: number;
  minCabins?: number;
  maxCabins?: number;
  minBerths?: number;
  maxBerths?: number;
  minYear?: number;
  maxYear?: number;
  minWc?: number;
  minPeople?: number;
  minBeam?: number;
  maxBeam?: number;
  minDraught?: number;
  maxDraught?: number;
  equipmentIds?: string;
  countryPid?: string;
  sailingAreaPids?: string;
  basePids?: string;
  page?: number;
  trackedPage?: number;
  sortBy?: MarketSortColumn;
  sortOrder?: 'asc' | 'desc';
}

const defaultFilters: MarketFilters = {
  search: '',
  kind: '',
  models: [],
  homeBase: '',
  charterNames: [],
  equipmentIds: [],
  sailingAreaPids: [],
  basePids: [],
};

const NUMERIC_FILTER_KEYS = [
  'minLength',
  'maxLength',
  'minCabins',
  'maxCabins',
  'minBerths',
  'maxBerths',
  'minYear',
  'maxYear',
  'minWc',
  'minPeople',
  'minBeam',
  'maxBeam',
  'minDraught',
  'maxDraught',
] as const;

function parseSearchParams(params: MarketSearchParams): {
  filters: MarketFilters;
  tab: MarketTab;
  browsePage: number;
  trackedPage: number;
  sort: SortState;
} {
  const equipmentIds = params.equipmentIds
    ? params.equipmentIds
        .split(',')
        .map(Number)
        .filter((n) => !isNaN(n))
    : [];

  // Sanitize invalid cascade combos from URL-direct edits: sailing areas
  // without a country (or bases without sailing areas) are meaningless
  // because the UI disables those dropdowns. Drop the orphans so the API
  // doesn't silently apply something the user can't see in the UI.
  const sanitizedCountryPid = params.countryPid;
  const sailingAreaPids =
    sanitizedCountryPid && params.sailingAreaPids
      ? params.sailingAreaPids.split(',').filter(Boolean)
      : [];
  const basePids =
    sailingAreaPids.length > 0 && params.basePids ? params.basePids.split(',').filter(Boolean) : [];

  const filters: MarketFilters = {
    search: params.search ?? '',
    kind: params.kind ?? '',
    models: params.models ? params.models.split(',').filter(Boolean) : [],
    homeBase: params.homeBase ?? '',
    charterNames: params.charterNames ? params.charterNames.split(',').filter(Boolean) : [],
    equipmentIds,
    countryPid: sanitizedCountryPid,
    sailingAreaPids,
    basePids,
  };

  for (const key of NUMERIC_FILTER_KEYS) {
    if (params[key] !== undefined) {
      filters[key] = params[key];
    }
  }

  return {
    filters,
    tab: params.tab ?? 'browse',
    browsePage: params.tab === 'tracked' ? 1 : (params.page ?? 1),
    trackedPage: params.tab === 'tracked' ? (params.page ?? 1) : (params.trackedPage ?? 1),
    sort: { by: params.sortBy, order: params.sortOrder === 'desc' ? 'desc' : 'asc' },
  };
}

function filtersToSearchParams(
  filters: MarketFilters,
  tab: MarketTab,
  browsePage: number,
  trackedPage: number,
  sort: SortState
): MarketSearchParams {
  const params: MarketSearchParams = {};

  // Only include non-default values to keep URL clean
  if (tab !== 'browse') params.tab = tab;
  if (filters.search) params.search = filters.search;
  if (filters.kind) params.kind = filters.kind;
  if (filters.models.length > 0) params.models = filters.models.join(',');
  if (filters.homeBase) params.homeBase = filters.homeBase;
  if (filters.charterNames.length > 0) params.charterNames = filters.charterNames.join(',');

  for (const key of NUMERIC_FILTER_KEYS) {
    if (filters[key] !== undefined) params[key] = filters[key];
  }

  if (filters.equipmentIds.length > 0) params.equipmentIds = filters.equipmentIds.join(',');
  if (filters.countryPid) params.countryPid = filters.countryPid;
  if (filters.sailingAreaPids.length > 0)
    params.sailingAreaPids = filters.sailingAreaPids.join(',');
  if (filters.basePids.length > 0) params.basePids = filters.basePids.join(',');

  if (isMarketSortColumn(sort.by)) {
    params.sortBy = sort.by;
    params.sortOrder = sort.order;
  }

  const activePage = tab === 'tracked' ? trackedPage : browsePage;
  if (activePage > 1) params.page = activePage;

  return params;
}

function buildApiParams(
  filters: MarketFilters,
  debounced: { search: string; homeBase: string },
  page: number,
  sort: SortState
): Record<string, string | number> {
  const params: Record<string, string | number> = {};

  if (debounced.search) params['search'] = debounced.search;
  if (filters.kind) params['kind'] = filters.kind;
  if (filters.models.length > 0) params['models'] = filters.models.join(',');
  if (debounced.homeBase) params['homeBase'] = debounced.homeBase;
  if (filters.charterNames.length > 0) params['charterNames'] = filters.charterNames.join(',');

  for (const key of NUMERIC_FILTER_KEYS) {
    if (filters[key] !== undefined) params[key] = filters[key];
  }

  if (filters.equipmentIds.length > 0) params['equipmentIds'] = filters.equipmentIds.join(',');
  if (filters.countryPid) params['countryPid'] = filters.countryPid;
  if (filters.sailingAreaPids.length > 0)
    params['sailingAreaPids'] = filters.sailingAreaPids.join(',');
  if (filters.basePids.length > 0) params['basePids'] = filters.basePids.join(',');
  if (sort.by) {
    params['sortBy'] = sort.by;
    params['sortOrder'] = sort.order;
  }
  params['page'] = page;
  params['limit'] = 20;

  return params;
}

export function useMarketFilters(initialSearch: MarketSearchParams = {}) {
  const navigate = useNavigate();
  const orgPid = useOrgParam();
  const initial = useMemo(() => parseSearchParams(initialSearch), []);

  const [filters, setFilters] = useState<MarketFilters>(initial.filters);
  const [tab, setTabState] = useState(initial.tab);
  const [browsePage, setBrowsePage] = useState(initial.browsePage);
  const [trackedPage, setTrackedPage] = useState(initial.trackedPage);
  const [sort, setSort] = useState<SortState>(initial.sort);

  const debouncedSearch = useDebounce(filters.search, 300);
  const debouncedHomeBase = useDebounce(filters.homeBase, 300);

  const debounced = useMemo(
    () => ({
      search: debouncedSearch,
      homeBase: debouncedHomeBase,
    }),
    [debouncedSearch, debouncedHomeBase]
  );

  // Sync to URL after debounce settles — use debounced text values so URL doesn't thrash
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const filtersForUrl: MarketFilters = {
      ...filters,
      search: debouncedSearch,
      homeBase: debouncedHomeBase,
    };

    const searchParams = filtersToSearchParams(filtersForUrl, tab, browsePage, trackedPage, sort);

    void navigate({
      to: '/$orgPid/market-explorer',
      params: { orgPid },
      search: searchParams,
      replace: true,
    });
  }, [
    debouncedSearch,
    debouncedHomeBase,
    filters.charterNames,
    filters.kind,
    filters.models,
    filters.minLength,
    filters.maxLength,
    filters.minCabins,
    filters.maxCabins,
    filters.minBerths,
    filters.maxBerths,
    filters.minYear,
    filters.maxYear,
    filters.minWc,
    filters.minPeople,
    filters.minBeam,
    filters.maxBeam,
    filters.minDraught,
    filters.maxDraught,
    filters.equipmentIds,
    filters.countryPid,
    filters.sailingAreaPids,
    filters.basePids,
    tab,
    browsePage,
    trackedPage,
    // Without these the sort never reaches the URL when it is changed on page 1
    // (the common case): no other dependency changes, so the effect never runs.
    sort.by,
    sort.order,
    navigate,
  ]);

  const setFilter = useCallback(
    <K extends keyof MarketFilters>(key: K, value: MarketFilters[K]) => {
      setFilters((prev) => {
        const next = { ...prev, [key]: value };
        // Cascade: changing kind clears model selection
        if (key === 'kind') next.models = [];
        // Cascade: changing/clearing a parent location level invalidates
        // children. Defensive — the cascade component also calls these
        // explicitly, but URL-direct edits or other callers bypass that.
        if (key === 'countryPid') {
          next.sailingAreaPids = [];
          next.basePids = [];
        }
        if (key === 'sailingAreaPids') {
          next.basePids = [];
        }
        return next;
      });
      setBrowsePage(1);
      setTrackedPage(1);
    },
    []
  );

  const setTab = useCallback((newTab: MarketTab) => {
    setTabState(newTab);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({ ...defaultFilters });
    setBrowsePage(1);
    setTrackedPage(1);
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.kind) count++;
    if (filters.models.length > 0) count++;
    if (filters.homeBase) count++;
    if (filters.charterNames.length > 0) count++;
    for (const key of NUMERIC_FILTER_KEYS) {
      if (filters[key] !== undefined) count++;
    }
    if (filters.equipmentIds.length > 0) count++;
    if (filters.countryPid) count++;
    if (filters.sailingAreaPids.length > 0) count++;
    if (filters.basePids.length > 0) count++;
    return count;
  }, [filters]);

  const toBrowseParams = useCallback(
    () => buildApiParams(filters, debounced, browsePage, sort),
    [filters, debounced, browsePage, sort]
  );

  const toTrackedParams = useCallback(
    () => buildApiParams(filters, debounced, trackedPage, sort),
    [filters, debounced, trackedPage, sort]
  );

  /** Cycle a column header. Sorting reorders the whole market, so go back to page 1. */
  const toggleSort = useCallback((column: string) => {
    setSort((current) => nextSort(current, column));
    setBrowsePage(1);
    setTrackedPage(1);
  }, []);

  return {
    filters,
    tab,
    browsePage,
    trackedPage,
    setBrowsePage,
    setTrackedPage,
    setTab,
    setFilter,
    resetFilters,
    activeFilterCount,
    toBrowseParams,
    toTrackedParams,
    sort,
    toggleSort,
  };
}
